// WCAG 2.x relative-luminance contrast ratio helpers.
// Used by the Appearance preferences "Text color" picker to prevent users
// from selecting a color that would make the UI unreadable.

const HEX_RE = /^#?([0-9a-f]{3}|[0-9a-f]{6})$/i;

export interface RGB {
  r: number;
  g: number;
  b: number;
}

export function parseHexColor(hex: string): RGB | null {
  if (!HEX_RE.test(hex.trim())) return null;
  let h = hex.trim().replace(/^#/, '');
  if (h.length === 3) h = h.split('').map((c) => c + c).join('');
  const n = parseInt(h, 16);
  return {
    r: (n >> 16) & 0xff,
    g: (n >> 8) & 0xff,
    b: n & 0xff,
  };
}

function srgbChannelToLinear(channel: number): number {
  const c = channel / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

export function relativeLuminance({ r, g, b }: RGB): number {
  const rl = srgbChannelToLinear(r);
  const gl = srgbChannelToLinear(g);
  const bl = srgbChannelToLinear(b);
  return 0.2126 * rl + 0.7152 * gl + 0.0722 * bl;
}

export function contrastRatio(fg: RGB, bg: RGB): number {
  const l1 = relativeLuminance(fg);
  const l2 = relativeLuminance(bg);
  const [hi, lo] = l1 >= l2 ? [l1, l2] : [l2, l1];
  return (hi + 0.05) / (lo + 0.05);
}

export function contrastRatioHex(fgHex: string, bgHex: string): number | null {
  const fg = parseHexColor(fgHex);
  const bg = parseHexColor(bgHex);
  if (!fg || !bg) return null;
  return contrastRatio(fg, bg);
}

// Parses "rgb(r, g, b)" / "rgba(r, g, b, a)" strings as returned by
// getComputedStyle. Ignores alpha. Returns null on parse failure.
export function parseRgbString(value: string): RGB | null {
  const match = value.match(/rgba?\(\s*(\d+)\s*,\s*(\d+)\s*,\s*(\d+)/i);
  if (!match) return null;
  return {
    r: parseInt(match[1], 10),
    g: parseInt(match[2], 10),
    b: parseInt(match[3], 10),
  };
}

// WCAG AA threshold for normal body text.
export const WCAG_AA_NORMAL = 4.5;
